import { useMemo } from 'react';
import { Checkbox } from '@shared/components';
import { AMOUNT_BASIS_LABELS } from '../utils/amountBasis';
import type { AmountBasis } from '../utils/amountBasis';
import { itemSelectionRows } from '../utils/selectionTableRows';
import type { ItemSelectionRow } from '../utils/selectionTableRows';
import type { SelectionUnit } from '../utils/invoiceSelection';
import type { SoldService } from '../types/agreements.types';

interface Props {
    service: SoldService;
    flexible: boolean;
    selectedKeys: ReadonlySet<string>;
    onToggle: (unit: SelectionUnit) => void;
    locked?: boolean;
    basis?: AmountBasis;
    readOnlyItems?: boolean;
}

const GRID_PRICED =
    'grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center @min-[22rem]:grid-cols-[2.5rem_minmax(0,1fr)_3.5rem_minmax(6.5rem,auto)]';
const GRID_UNPRICED = 'grid grid-cols-[2.5rem_minmax(0,1fr)] items-center @min-[22rem]:grid-cols-[2.5rem_minmax(0,1fr)_3.5rem]';
const QTY_CELL = 'col-start-2 row-start-2 @min-[22rem]:col-start-3 @min-[22rem]:row-start-1';
const NET_CELL = 'col-start-3 row-start-1 pl-2 pr-3 text-right @min-[22rem]:col-start-4';

function ItemRow({
    row,
    priced,
    locked,
    basis,
    readOnlyItems,
    onToggle,
}: {
    row: ItemSelectionRow;
    priced: boolean;
    locked: boolean;
    basis: AmountBasis;
    readOnlyItems: boolean;
    onToggle: (unit: SelectionUnit) => void;
}) {
    return (
        <li className={`${priced ? GRID_PRICED : GRID_UNPRICED} py-1 text-sm ${row.tone === 'selected' ? 'bg-mp-primary/10' : ''}`}>
            <span className="col-start-1 row-start-1 flex justify-center">
                {!readOnlyItems && (
                    <Checkbox
                        ariaLabel={`Select ${row.item.name}`}
                        checked={row.checked}
                        disabled={locked || !row.selectable}
                        onChange={() => onToggle(row.unit)}
                    />
                )}
            </span>
            <span className="col-start-2 row-start-1 min-w-0 break-words text-mp-foreground">{row.item.name}</span>
            <span className={`${QTY_CELL} text-mp-muted`}>
                <span className="@min-[22rem]:sr-only">Qty </span>
                {row.item.quantity}
            </span>
            {priced && (
                <span className={`${NET_CELL} tabular-nums text-mp-foreground`}>
                    <span className="sr-only">{AMOUNT_BASIS_LABELS[basis].column} </span>
                    {row.net}
                </span>
            )}
        </li>
    );
}

/** The expanded service row's content. `isItemSelectable` already encodes
 *  total-priced, single-mode and claimed disablement, so this stays presentational. */
export function SoldServiceItemsPanel({ service, flexible, selectedKeys, onToggle, locked = false, basis = 'net', readOnlyItems = false }: Props) {
    const priced = readOnlyItems || service.pricedAt === 'item';
    const rows = useMemo(
        () => itemSelectionRows(service, selectedKeys, flexible, { basis, priced }),
        [service, selectedKeys, flexible, basis, priced],
    );

    return (
        <div id={`sold-service-${service.id}-items`} className="@container space-y-2">
            {!priced && (
                <p className="text-sm text-mp-muted">
                    This service was sold at a single price and can only be invoiced whole.
                </p>
            )}
            {rows.length === 0 ? (
                <p className="text-sm text-mp-muted">No items were recorded on this service.</p>
            ) : (
                <>
                    <div aria-hidden="true" className={`${priced ? GRID_PRICED : GRID_UNPRICED} text-xs font-semibold uppercase tracking-wide text-mp-muted`}>
                        <span className="col-start-1 row-start-1" />
                        <span className="col-start-2 row-start-1">Name</span>
                        <span className={`${QTY_CELL} hidden @min-[22rem]:block`}>Qty</span>
                        {priced && <span className={NET_CELL}>{AMOUNT_BASIS_LABELS[basis].column}</span>}
                    </div>
                    <ul className="divide-y divide-mp-border">
                        {rows.map((row) => (
                            <ItemRow key={row.item.id} row={row} priced={priced} locked={locked} basis={basis} readOnlyItems={readOnlyItems} onToggle={onToggle} />
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
